import * as THREE from 'three';
import { gsap } from 'gsap';

export default class DeskScene {
  constructor(scene) {
    this.scene = scene;
    this.deskGroup = new THREE.Group();
    this.scene.add(this.deskGroup);
    this.setupDeskScene();
    this.startClock();
    this.clock = new THREE.Clock();
  }

  setupDeskScene() {
    this.createEnvironment();
    this.createDesk();
    this.createMonitorStand();
    this.createPeripherals();
    this.createPlant();
    this.createWallArt();
    this.createClock();
    this.createNotebook();
    this.createTrashBin();
    this.createClutter();
    this.createGlobe();
    this.createCalendar();
    this.createRubberDuck();
  }

  createEnvironment() {
    // Create floor
    const floorGeometry = new THREE.PlaneGeometry(20, 20);
    const floorMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x444444,
      roughness: 0.8,
      metalness: 0.2
    });
    const floor = new THREE.Mesh(floorGeometry, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0;
    floor.receiveShadow = true;
    this.scene.add(floor);
    
    // Create wall behind desk - moved closer to desk
    const wallGeometry = new THREE.PlaneGeometry(20, 10);
    const wallMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x2a2a2a,
      roughness: 0.9,
      metalness: 0.1
    });
    const wall = new THREE.Mesh(wallGeometry, wallMaterial);
    wall.position.set(0, 5, -0.5);
    wall.receiveShadow = true;
    this.scene.add(wall);

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4); // Increased from default
    this.scene.add(ambientLight);

    // Add spotlights for artwork
    const createSpotlight = (x) => {
      const spotlight = new THREE.SpotLight(0xffffff, 2);
      spotlight.position.set(x, 3.5, 1);
      spotlight.target.position.set(x, 2, -0.45);
      spotlight.angle = Math.PI / 6;
      spotlight.penumbra = 0.5;
      spotlight.decay = 1.5;
      spotlight.distance = 4;
      spotlight.castShadow = true;
      this.scene.add(spotlight);
      this.scene.add(spotlight.target);

      // Add subtle volumetric light effect
      const lightGeometry = new THREE.CylinderGeometry(0.08, 0.8, 8, 16);
      const lightMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.17,
        blending: THREE.AdditiveBlending
      });
      const lightCone = new THREE.Mesh(lightGeometry, lightMaterial);
      lightCone.position.set(x, 4.5, 0.5);
      lightCone.rotation.x = Math.PI / 4;
      this.scene.add(lightCone);
    };

    // Create spotlights for left and right paintings
    createSpotlight(-2);
    createSpotlight(2);

    // Add a soft rim light from behind
    const rimLight = new THREE.DirectionalLight(0x64ffda, 0.3);
    rimLight.position.set(0, 2, -2);
    this.scene.add(rimLight);
  }

  createDesk() {
    // Create desk
    const deskGeometry = new THREE.BoxGeometry(2, 0.08, 1);
    const deskMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xffffff,
      roughness: 0.2,
      metalness: 0.1
    });
    this.desk = new THREE.Mesh(deskGeometry, deskMaterial);
    this.desk.position.set(0, 0.75, 0);
    this.desk.castShadow = true;
    this.desk.receiveShadow = true;
    this.deskGroup.add(this.desk);
    
    // Create desk legs
    const legMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xcccccc,
      roughness: 0.3,
      metalness: 0.7
    });
    
    // Left leg
    const legLeftGeometry = new THREE.BoxGeometry(0.08, 0.74, 0.6);
    const legLeft = new THREE.Mesh(legLeftGeometry, legMaterial);
    legLeft.position.set(-0.9, 0.37, 0);
    legLeft.castShadow = true;
    this.deskGroup.add(legLeft);
    
    // Right leg
    const legRight = new THREE.Mesh(legLeftGeometry, legMaterial);
    legRight.position.set(0.9, 0.37, 0);
    legRight.castShadow = true;
    this.deskGroup.add(legRight);
  }

  createMonitorStand() {
    // Create monitor stand
    const standGeometry = new THREE.BoxGeometry(0.08, 0.5, 0.08);
    const standMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x666666,
      roughness: 0.2,
      metalness: 0.8
    });
    const stand = new THREE.Mesh(standGeometry, standMaterial);
    stand.position.set(0, 1, -0.25);
    stand.castShadow = true;
    this.deskGroup.add(stand);
    
    // Create monitor base
    const baseGeometry = new THREE.BoxGeometry(0.3, 0.03, 0.2);
    const base = new THREE.Mesh(baseGeometry, standMaterial);
    base.position.set(0, 0.78, -0.25);
    base.castShadow = true;
    this.deskGroup.add(base);
  }

  createPeripherals() {
    // Create keyboard
    const keyboardGeometry = new THREE.BoxGeometry(0.4, 0.02, 0.15);
    const keyboardMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.5,
      metalness: 0.5
    });
    this.keyboard = new THREE.Mesh(keyboardGeometry, keyboardMaterial);
    this.keyboard.position.set(0, 0.81, 0.15);
    this.keyboard.rotation.y = -Math.PI;
    this.deskGroup.add(this.keyboard);

    // Add keyboard LED
    const ledGeometry = new THREE.BoxGeometry(0.01, 0.01, 0.01);
    const ledMaterial = new THREE.MeshBasicMaterial({
      color: 0x64ffda,
      transparent: true,
      opacity: 0.8
    });
    this.keyboardLED = new THREE.Mesh(ledGeometry, ledMaterial);
    this.keyboardLED.position.set(0.18, 0.015, 0.06);
    this.keyboard.add(this.keyboardLED);

    // Add keyboard underglow
    const glowGeometry = new THREE.PlaneGeometry(0.4, 0.15);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x64ffda,
      transparent: true,
      opacity: 0.1,
      blending: THREE.AdditiveBlending
    });
    this.keyboardGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    this.keyboardGlow.position.set(0, -0.01, 0);
    this.keyboardGlow.rotation.x = -Math.PI / 2;
    this.keyboard.add(this.keyboardGlow);

    // Animate keyboard LED and glow
    this.animateKeyboardLED();

    // Create mouse
    const mouseGeometry = new THREE.BoxGeometry(0.06, 0.03, 0.1);
    // Convert to buffer geometry and modify vertices
    const positionAttribute = mouseGeometry.getAttribute('position');
    const positions = positionAttribute.array;
    
    // Modify top vertices to create curved surface
    for (let i = 0; i < positions.length; i += 3) {
        if (positions[i + 1] > 0) { // if y > 0 (top vertices)
            positions[i + 1] *= 1.5; // increase height
        }
    }
    
    positionAttribute.needsUpdate = true;
    mouseGeometry.computeVertexNormals();
    
    const mouseMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.5,
      metalness: 0.5
    });
    this.mouse3D = new THREE.Mesh(mouseGeometry, mouseMaterial);
    this.mouse3D.position.set(0.3, 0.81, 0.15);
    this.deskGroup.add(this.mouse3D);

    // Add mouse LED
    const mouseLEDGeometry = new THREE.BoxGeometry(0.01, 0.01, 0.01);
    const mouseLEDMaterial = new THREE.MeshBasicMaterial({
      color: 0x64ffda,
      transparent: true,
      opacity: 0.8
    });
    this.mouseLED = new THREE.Mesh(mouseLEDGeometry, mouseLEDMaterial);
    this.mouseLED.position.set(0, 0.02, -0.02);
    this.mouse3D.add(this.mouseLED);

    // Animate mouse LED
    this.animateMouseLED();
  }

  animateKeyboardLED() {
    // Pulsing LED
    gsap.to(this.keyboardLED.material, {
      opacity: 0.2,
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut"
    });

    // Breathing underglow
    gsap.to(this.keyboardGlow.material, {
      opacity: 0.05,
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut"
    });
  }

  animateMouseLED() {
    gsap.to(this.mouseLED.material, {
      opacity: 0.2,
      duration: 1.5,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut"
    });
  }

  createPlant() {
    // Create pot
    const potGeometry = new THREE.CylinderGeometry(0.05, 0.06, 0.08, 16);
    const potMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x995533,
      roughness: 0.8,
      metalness: 0.1
    });
    const pot = new THREE.Mesh(potGeometry, potMaterial);
    pot.position.set(-0.7, 0.84, 0.2);
    pot.castShadow = true;
    this.deskGroup.add(pot);
    
    // Create plant group
    this.plant = new THREE.Group();
    this.plant.name = 'plant';
    this.plant.position.set(-0.7, 0.88, 0.2);
    this.deskGroup.add(this.plant);
    
    // Create leaves
    const leafGeometry = new THREE.SphereGeometry(0.06, 8, 8);
    const leafMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x225522,
      roughness: 1.0,
      metalness: 0.0
    });

    const leaves = [
      { pos: [0, 0.06, 0], scale: [1, 1.5, 1] },
      { pos: [0.05, 0.02, 0.03], scale: [0.7, 1.2, 0.7] },
      { pos: [-0.04, 0.02, 0.05], scale: [0.8, 1.3, 0.8] }
    ];

    leaves.forEach(leaf => {
      const leafMesh = new THREE.Mesh(leafGeometry, leafMaterial);
      leafMesh.position.set(...leaf.pos);
      leafMesh.scale.set(...leaf.scale);
      leafMesh.castShadow = true;
      this.plant.add(leafMesh);
    });
  }

  createWallArt() {
    // Create frame for Tesla-inspired art
    const frame1 = new THREE.Group();
    const frameGeometry = new THREE.BoxGeometry(0.8, 1, 0.05);
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: 0x2c2c2c,
      roughness: 0.5,
      metalness: 0.3
    });
    const frameBox = new THREE.Mesh(frameGeometry, frameMaterial);
    
    // Create canvas texture for Tesla art
    const canvas1 = document.createElement('canvas');
    canvas1.width = 512;
    canvas1.height = 512;
    const ctx1 = canvas1.getContext('2d');
    
    // Create Tesla-inspired triangle art
    ctx1.fillStyle = '#1a1a1a';
    ctx1.fillRect(0, 0, 512, 512);
    
    // Draw nested triangles with decreasing brightness
    const centerX = 256;
    const startY = 100;  // Moved up to accommodate numbers at bottom
    const triangleHeight = 300;  // Increased size
    const triangleWidth = 400;   // Increased size
    
    // Function to draw a single triangle with opacity
    const drawTriangle = (opacity) => {
      ctx1.beginPath();
      ctx1.moveTo(centerX, startY);
      ctx1.lineTo(centerX + triangleWidth/2, startY + triangleHeight);
      ctx1.lineTo(centerX - triangleWidth/2, startY + triangleHeight);
      ctx1.closePath();
      ctx1.strokeStyle = `rgba(100, 255, 218, ${opacity})`;
      ctx1.stroke();
    };

    // Draw 9 triangles with decreasing opacity
    ctx1.shadowColor = '#64ffda';
    ctx1.shadowBlur = 30;
    ctx1.lineWidth = 8;

    for (let i = 0; i < 9; i++) {
      const scale = 1 - (i * 0.1);  // Scale down each triangle
      ctx1.save();
      ctx1.translate(centerX, startY + triangleHeight/2);
      ctx1.scale(scale, scale);
      ctx1.translate(-centerX, -(startY + triangleHeight/2));
      drawTriangle(1 - (i * 0.1));  // Decrease opacity with each triangle
      ctx1.restore();
    }
    
    // Add numbers at the bottom
    ctx1.font = 'bold 72px Arial';
    ctx1.fillStyle = '#64ffda';
    ctx1.textAlign = 'center';
    ctx1.textBaseline = 'middle';
    ctx1.shadowBlur = 20;
    
    // Position numbers evenly at the bottom
    const numberY = startY + triangleHeight + 60;  // Below the triangle
    const numberSpacing = 120;  // Space between numbers
    
    ctx1.fillText('3', centerX - numberSpacing, numberY);
    ctx1.fillText('6', centerX, numberY);
    ctx1.fillText('9', centerX + numberSpacing, numberY);

    const paintingTexture1 = new THREE.CanvasTexture(canvas1);
    const paintingMaterial = new THREE.MeshStandardMaterial({
      map: paintingTexture1,
      roughness: 0.5
    });

    const paintingGeometry = new THREE.PlaneGeometry(0.7, 0.9);
    const painting = new THREE.Mesh(paintingGeometry, paintingMaterial);
    painting.position.z = 0.026;

    frame1.add(frameBox);
    frame1.add(painting);
    frame1.position.set(-2, 2, -0.45);
    this.scene.add(frame1);

    // Create planet art
    const frame2 = frame1.clone();
    const canvas2 = document.createElement('canvas');
    canvas2.width = 512;
    canvas2.height = 512;
    const ctx2 = canvas2.getContext('2d');

    // Background
    ctx2.fillStyle = '#1a1a1a';
    ctx2.fillRect(0, 0, 512, 512);

    // Draw planet
    ctx2.shadowColor = '#64ffda';
    ctx2.shadowBlur = 30;
    ctx2.beginPath();
    ctx2.arc(256, 256, 120, 0, Math.PI * 2);
    ctx2.fillStyle = '#64ffda';
    ctx2.fill();

    // Add rings
    ctx2.strokeStyle = '#ffffff';
    ctx2.lineWidth = 3;
    for (let i = 0; i < 3; i++) {
      ctx2.beginPath();
      ctx2.ellipse(
        256, 256,
        160 + i * 20, (160 + i * 20) * 0.3,
        Math.PI * 0.15,
        0, Math.PI * 2
      );
      ctx2.stroke();
    }

    // Add stars
    ctx2.fillStyle = '#ffffff';
    for (let i = 0; i < 100; i++) {
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const size = Math.random() * 2 + 1;
      
      // Don't draw stars over the planet
      const dx = x - 256;
      const dy = y - 256;
      if (Math.sqrt(dx * dx + dy * dy) > 130) {
        ctx2.beginPath();
        ctx2.arc(x, y, size, 0, Math.PI * 2);
        ctx2.fill();
      }
    }

    const paintingTexture2 = new THREE.CanvasTexture(canvas2);
    frame2.children[1].material = new THREE.MeshStandardMaterial({
      map: paintingTexture2,
      roughness: 0.5
    });

    frame2.position.set(2, 2, -0.45);
    this.scene.add(frame2);
  }

  createClock() {
    const clockGroup = new THREE.Group();
    
    // Create clock base
    const baseGeometry = new THREE.BoxGeometry(0.2, 0.1, 0.05);
    const baseMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.2,
      metalness: 0.8
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    clockGroup.add(base);

    // Create digital display
    const displayGeometry = new THREE.PlaneGeometry(0.18, 0.08);
    const displayCanvas = document.createElement('canvas');
    displayCanvas.width = 256;
    displayCanvas.height = 128;
    const displayTexture = new THREE.CanvasTexture(displayCanvas);
    const displayMaterial = new THREE.MeshBasicMaterial({
      map: displayTexture,
      transparent: true
    });
    this.clockDisplay = new THREE.Mesh(displayGeometry, displayMaterial);
    this.clockDisplay.position.z = 0.026;
    clockGroup.add(this.clockDisplay);

    // Make clock interactive
    this.clockDisplay.userData.isInteractive = true;
    this.clockDisplay.name = 'clock';

    // Position the clock on the desk
    clockGroup.position.set(0.7, 0.81, -0.2);
    clockGroup.rotation.x = -0.2;
    this.deskGroup.add(clockGroup);

    // Store canvas context for updates
    this.clockCanvas = displayCanvas;
    this.clockContext = displayCanvas.getContext('2d');
    this.updateClockDisplay();
  }

  updateClockDisplay() {
    const ctx = this.clockContext;
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 256, 128);

    // Get current time and timezone
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // Get timezone name
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    ctx.font = 'bold 48px Arial';
    ctx.fillStyle = '#64ffda';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(timeString, 128, 64);

    // Store timezone in userData for use in handleObjectAnimation
    this.clockDisplay.userData.timezone = timezone;

    this.clockDisplay.material.map.needsUpdate = true;
  }

  startClock() {
    setInterval(() => this.updateClockDisplay(), 1000);
  }

  createNotebook() {
    // Create notebook
    const notebookGeometry = new THREE.BoxGeometry(0.2, 0.02, 0.25);
    const notebookMaterial = new THREE.MeshStandardMaterial({
      color: 0xeeeeee,
      roughness: 0.8,
      metalness: 0.1
    });
    const notebook = new THREE.Mesh(notebookGeometry, notebookMaterial);
    notebook.position.set(-0.4, 0.81, 0.1);
    notebook.rotation.y = 0.3;
    this.deskGroup.add(notebook);

    // Add some "lines" to the notebook
    const linesGeometry = new THREE.PlaneGeometry(0.18, 0.23);
    const linesCanvas = document.createElement('canvas');
    linesCanvas.width = 256;
    linesCanvas.height = 256;
    const ctx = linesCanvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 256, 256);
    ctx.strokeStyle = '#999999';
    ctx.lineWidth = 1;

    for (let i = 0; i < 10; i++) {
      ctx.beginPath();
      ctx.moveTo(0, 25 + i * 20);
      ctx.lineTo(256, 25 + i * 20);
      ctx.stroke();
    }

    const linesTexture = new THREE.CanvasTexture(linesCanvas);
    const linesMaterial = new THREE.MeshStandardMaterial({
      map: linesTexture,
      roughness: 0.8
    });
    const lines = new THREE.Mesh(linesGeometry, linesMaterial);
    lines.position.set(-0.4, 0.821, 0.1);
    lines.rotation.x = -Math.PI / 2;
    lines.rotation.z = 0.3;
    this.deskGroup.add(lines);
  }

  createTrashBin() {
    // Create trash bin body
    const binGeometry = new THREE.CylinderGeometry(0.15, 0.12, 0.3, 12);
    const binMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.7,
      metalness: 0.3
    });
    this.trashBin = new THREE.Mesh(binGeometry, binMaterial);
    this.trashBin.position.set(1.3, 0.15, -0.2);
    this.deskGroup.add(this.trashBin);

    // Add some crumpled paper
    const paperGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const paperMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 1,
      metalness: 0
    });

    const paper1 = new THREE.Mesh(paperGeometry, paperMaterial);
    paper1.position.set(1.3, 0.2, -0.2);
    paper1.scale.y = 0.7;
    this.deskGroup.add(paper1);
  }

  createClutter() {
    // Create coffee cup
    const cupGeometry = new THREE.CylinderGeometry(0.04, 0.03, 0.1, 12);
    const cupMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.3,
      metalness: 0.7
    });
    const cup = new THREE.Mesh(cupGeometry, cupMaterial);
    cup.position.set(-0.6, 0.81, -0.1);
    this.deskGroup.add(cup);

    // Create some sticky notes
    const stickyGeometry = new THREE.BoxGeometry(0.08, 0.01, 0.08);
    const stickyMaterial = new THREE.MeshStandardMaterial({
      color: 0xffeb3b,
      roughness: 0.9,
      metalness: 0
    });
    const stickyNotes = new THREE.Mesh(stickyGeometry, stickyMaterial);
    stickyNotes.position.set(0.6, 0.81, 0.2);
    stickyNotes.rotation.y = 0.2;
    this.deskGroup.add(stickyNotes);

    // Create pen holder - moved closer to clock
    const holderGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.08, 8);
    const holderMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.5,
      metalness: 0.5
    });
    const holder = new THREE.Mesh(holderGeometry, holderMaterial);
    holder.position.set(0.85, 0.85, -0.15);
    this.deskGroup.add(holder);

    // Create pens in holder
    const penGeometry = new THREE.CylinderGeometry(0.004, 0.004, 0.1, 8);
    const penMaterial = new THREE.MeshStandardMaterial({
      color: 0x111111,
      roughness: 0.5,
      metalness: 0.5
    });

    // Add a few pens in the holder at slight angles
    const penPositions = [
      { x: 0.01, z: 0.01, rotY: 0.1 },
      { x: -0.01, z: -0.01, rotY: -0.1 },
      { x: 0.01, z: -0.01, rotY: 0.05 }
    ];

    penPositions.forEach(pos => {
      const pen = new THREE.Mesh(penGeometry, penMaterial);
      pen.position.set(
        0.85 + pos.x,
        0.89,
        -0.15 + pos.z
      );
      pen.rotation.set(0, pos.rotY, 0);
      this.deskGroup.add(pen);
    });
  }

  createGlobe() {
    // Create globe group
    this.globeGroup = new THREE.Group();
    
    // Create stand base
    const baseGeometry = new THREE.CylinderGeometry(0.05, 0.07, 0.02, 16);
    const baseMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.7,
      metalness: 0.3
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    this.globeGroup.add(base);

    // Create stand stem
    const stemGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.15, 8);
    const stem = new THREE.Mesh(stemGeometry, baseMaterial);
    stem.position.y = 0.085;
    this.globeGroup.add(stem);

    // Create globe holder (the ring that holds the globe)
    const holderGeometry = new THREE.TorusGeometry(0.08, 0.005, 16, 32);
    const holderMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.5,
      metalness: 0.7
    });
    this.globeHolder = new THREE.Mesh(holderGeometry, holderMaterial);
    this.globeHolder.rotation.x = Math.PI / 2;
    this.globeHolder.position.y = 0.17;
    this.globeGroup.add(this.globeHolder);

    // Create the globe sphere
    const globeGeometry = new THREE.SphereGeometry(0.07, 32, 32);
    const globeCanvas = document.createElement('canvas');
    globeCanvas.width = 512;
    globeCanvas.height = 256;
    const ctx = globeCanvas.getContext('2d');

    // Fill with a clean ocean color
    ctx.fillStyle = '#1a4d7c';
    ctx.fillRect(0, 0, 512, 256);

    // Define clean continent color
    const landColor = '#2d5a3c';

    // More accurate continent shapes with clean styling
    const continents = [
      // North America
      [[100, 45], [145, 45], [170, 65], [180, 85], [160, 100], [140, 110], [120, 105], [90, 90], [70, 70], [85, 50], [100, 45]],
      
      // South America
      [[155, 140], [170, 135], [185, 150], [190, 180], [180, 210], [165, 220], [150, 200], [145, 170], [155, 140]],
      
      // Europe
      [[240, 60], [270, 45], [290, 50], [300, 65], [280, 75], [260, 70], [240, 60]],
      
      // Africa
      [[240, 90], [270, 85], [300, 90], [320, 110], [320, 140], [300, 170], [280, 180], [250, 160], [235, 120], [240, 90]],
      
      // Asia
      [[300, 45], [340, 40], [380, 50], [420, 45], [450, 70], [440, 90], [420, 110], [380, 100], [350, 90], [320, 80], [300, 45]],
      
      // Australia
      [[420, 160], [450, 155], [460, 170], [450, 190], [430, 195], [410, 180], [420, 160]]
    ];

    // Draw continents with clean edges
    ctx.fillStyle = landColor;
    
    continents.forEach(continent => {
      ctx.beginPath();
      ctx.moveTo(continent[0][0], continent[0][1]);
      
      // Create smooth curves between points
      for (let i = 1; i < continent.length; i++) {
        const xc = (continent[i][0] + continent[i-1][0]) / 2;
        const yc = (continent[i][1] + continent[i-1][1]) / 2;
        ctx.quadraticCurveTo(continent[i-1][0], continent[i-1][1], xc, yc);
      }
      
      ctx.closePath();
      ctx.fill();
    });

    // Add subtle grid lines
    ctx.strokeStyle = '#64ffda';
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.2;

    // Latitude lines
    for (let i = 0; i < 256; i += 32) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(512, i);
      ctx.stroke();
    }

    // Longitude lines
    for (let i = 0; i < 512; i += 32) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, 256);
      ctx.stroke();
    }

    const globeTexture = new THREE.CanvasTexture(globeCanvas);
    const globeMaterial = new THREE.MeshStandardMaterial({
      map: globeTexture,
      roughness: 0.8,
      metalness: 0.1
    });

    this.globe = new THREE.Mesh(globeGeometry, globeMaterial);
    this.globe.rotation.x = 0.3; // Tilt like Earth
    this.globeHolder.add(this.globe);

    // Position the globe on the desk
    this.globeGroup.position.set(-0.8, 0.81, -0.2);
    this.globeGroup.rotation.y = 0.3;
    this.deskGroup.add(this.globeGroup);

    // Add subtle constant rotation
    this.animateGlobe();
  }

  animateGlobe() {
    // Very slow constant rotation
    gsap.to(this.globe.rotation, {
      y: Math.PI * 2,
      duration: 30,
      ease: "none",
      repeat: -1
    });
  }

  createCalendar() {
    // Create calendar base
    const calendarGeometry = new THREE.BoxGeometry(0.15, 0.02, 0.12);
    const calendarMaterial = new THREE.MeshStandardMaterial({
      color: 0xeeeeee,
      roughness: 0.8,
      metalness: 0.1
    });
    const calendar = new THREE.Mesh(calendarGeometry, calendarMaterial);
    calendar.position.set(-0.5, 0.81, -0.2);
    calendar.rotation.y = 0.2;
    this.deskGroup.add(calendar);

    // Create calendar display
    const displayGeometry = new THREE.PlaneGeometry(0.14, 0.11);
    const displayCanvas = document.createElement('canvas');
    displayCanvas.width = 256;
    displayCanvas.height = 256;
    const displayTexture = new THREE.CanvasTexture(displayCanvas);
    const displayMaterial = new THREE.MeshBasicMaterial({
      map: displayTexture,
      transparent: true
    });
    this.calendarDisplay = new THREE.Mesh(displayGeometry, displayMaterial);
    this.calendarDisplay.position.set(-0.5, 0.821, -0.2);
    this.calendarDisplay.rotation.x = -Math.PI / 2;
    this.calendarDisplay.rotation.z = 0.2;
    this.deskGroup.add(this.calendarDisplay);

    // Store canvas context for updates
    this.calendarCanvas = displayCanvas;
    this.calendarContext = displayCanvas.getContext('2d');
    this.updateCalendarDisplay();

    // Update calendar every minute
    setInterval(() => this.updateCalendarDisplay(), 60000);
  }

  updateCalendarDisplay() {
    const ctx = this.calendarContext;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 256, 256);

    const now = new Date();
    const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    // Draw month
    ctx.font = 'bold 32px Arial';
    ctx.fillStyle = '#333333';
    ctx.textAlign = 'center';
    ctx.fillText(months[now.getMonth()], 128, 64);

    // Draw day of week
    ctx.font = '24px Arial';
    ctx.fillStyle = '#666666';
    ctx.fillText(days[now.getDay()], 128, 96);

    // Draw date
    ctx.font = 'bold 64px Arial';
    ctx.fillStyle = '#64ffda';
    ctx.fillText(now.getDate().toString(), 128, 180);

    this.calendarDisplay.material.map.needsUpdate = true;
  }

  createRubberDuck() {
    this.duckGroup = new THREE.Group();
    this.duckGroup.name = 'duck';
    
    // Duck body
    const bodyGeometry = new THREE.SphereGeometry(0.03, 12, 12);
    const duckMaterial = new THREE.MeshStandardMaterial({
      color: 0xffeb3b,  // Yellow
      roughness: 0.9,
      metalness: 0.1
    });
    const body = new THREE.Mesh(bodyGeometry, duckMaterial);
    body.scale.z = 1.2;
    this.duckGroup.add(body);

    // Duck head
    const headGeometry = new THREE.SphereGeometry(0.02, 12, 12);
    const head = new THREE.Mesh(headGeometry, duckMaterial);
    head.position.set(0.02, 0.02, 0);
    this.duckGroup.add(head);

    // Duck bill
    const billGeometry = new THREE.ConeGeometry(0.01, 0.02, 4);
    const billMaterial = new THREE.MeshStandardMaterial({
      color: 0xff9800,  // Orange
      roughness: 0.9,
      metalness: 0.1
    });
    const bill = new THREE.Mesh(billGeometry, billMaterial);
    bill.rotation.z = Math.PI / 2;
    bill.position.set(0.04, 0.02, 0);
    this.duckGroup.add(bill);

    // Duck eyes
    const eyeGeometry = new THREE.SphereGeometry(0.005, 8, 8);
    const eyeMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000,
      roughness: 0.5,
      metalness: 0.5
    });
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.025, 0.025, 0.01);
    this.duckGroup.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(0.025, 0.025, -0.01);
    this.duckGroup.add(rightEye);

    // Position the duck in a slightly hidden spot
    this.duckGroup.position.set(0.9, 0.81, -0.3);
    this.duckGroup.rotation.y = -0.5;
    this.deskGroup.add(this.duckGroup);

    // Add subtle animation
    this.animateDuck();
  }

  animateDuck() {
    // Subtle bobbing animation
    gsap.to(this.duckGroup.position, {
      y: '+=0.01',
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut"
    });

    // Very slight rotation
    gsap.to(this.duckGroup.rotation, {
      y: '-=0.2',
      duration: 3,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut"
    });
  }

  getInteractiveObjects() {
    const objects = [this.keyboard, this.mouse3D, this.plant, this.duckGroup, this.trashBin, this.globe, this.clockDisplay].filter(obj => obj !== undefined);
    // console.log('Interactive objects:', objects);
    return objects;
  }

  handleObjectAnimation(object) {
    if (!object) return;

    if (object === this.clockDisplay) {
      // Create and show timezone popup
      console.log('Clock clicked');
      const popup = document.createElement('div');
      popup.className = 'timezone-popup';
      popup.textContent = `Sean is currently in ${object.userData.timezone} timezone`;
      document.body.appendChild(popup);

      // Position popup in the center of the screen initially
      popup.style.left = '50%';
      popup.style.top = '10%';
      popup.style.transform = 'translate(-50%, -50%)';

      // Remove popup after animation
      setTimeout(() => {
        popup.remove();
      }, 2000);

      return;
    }

    console.log('Handling object animation for:', object);
    console.log('Parent:', object.parent);
    if (object === this.keyboard) {
      gsap.to(object.position, {
        y: object.position.y - 0.01,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          if (window.soundManager?.initialized) {
            window.soundManager.playSound('keyPress');
          }
        }
      });
    } else if (object === this.mouse3D) {
      gsap.to(object.position, {
        y: object.position.y - 0.01,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          if (window.soundManager?.initialized) {
            window.soundManager.playSound('mouseClick');
          }
        }
      });
    } else if (object.parent === this.plant) {
      console.log('Plant clicked');
      const originalRotation = object.rotation.z;
      gsap.to(object.rotation, {
        z: originalRotation + 0.2,
        duration: 0.3,
        yoyo: true,
        repeat: 3,
        ease: "elastic.out(1, 0.3)",
        onStart: () => {
          if (window.soundManager?.initialized) {
            window.soundManager.playSound('plant');
          }
        }
      });
    } else if (object.parent === this.duckGroup) {
      console.log('Duck clicked');
      gsap.to(this.duckGroup.scale, {
        x: 1.2,
        y: 0.9,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        onStart: () => {
          if (window.soundManager?.initialized) {
            window.soundManager.playSound('duck');
          }
        }
      });
    } else if (object === this.trashBin) {
      console.log('Trash bin clicked');
      // Create a rattling animation
      gsap.to(object.rotation, {
        x: 0.1,
        z: 0.1,
        duration: 0.2,
        yoyo: true,
        repeat: 3,
        ease: "rough({ template: none, strength: 1, points: 20, taper: 'none', randomize: true, clamp: false })",
        onStart: () => {
          if (window.soundManager?.initialized) {
            window.soundManager.playSound('paperTrash');
          }
        }
      });
    } else if (object === this.globe || object.parent === this.globeHolder) {
      console.log('Globe clicked');
      // Speed up rotation temporarily
      gsap.to(this.globe.rotation, {
        y: this.globe.rotation.y + Math.PI * 4,
        duration: 2,
        ease: "power1.inOut",
        onComplete: () => {
          // Return to normal speed
          this.animateGlobe();
        }
      });
    }
  }

  update() {
    const delta = this.clock.getDelta();

    // Update computer hum volume based on time
    if (window.soundManager) {
      const computerVolume = 0.2 + Math.sin(this.clock.elapsedTime * 0.5) * 0.05;
      window.soundManager.setVolume('computerHum', computerVolume);
    }
  }

  cleanup() {
    window.soundManager.stopAllSounds();
  }
} 